import { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { unByKey } from 'ol/Observable';
import { LuColumns3 } from 'react-icons/lu';

import { useProject } from '@contexts/Project.context';
import { getMasterFeatures, getGrid } from '@/utils/api';
import { createFeature } from '@utils/map/helpers';
import { createTooltip } from '@utils/map/tooltip.overlay';
import { useWebGLVectorLayer } from '@hooks/useWebGLVectorLayer';
import { LayerFunction } from '../sidebars/sidebarElements/LayerSettings';

/**
 * Flat WebGL style for the grid polygons (cyan dashed outline + translucent fill).
 * Renders the heavy geometry on the GPU. The annotation count is shown via an HTML
 * overlay (see below) so it stays a fixed size and tracks the tile across zoom levels.
 */
const GRID_STYLE = {
	'stroke-color': 'rgba(0, 255, 255, 0.9)',
	'stroke-width': 2,
	// Dashed border keeps the grid visually distinct from the solid training-data outlines
	'stroke-line-dash': [6, 6],
	'fill-color': 'rgba(0, 255, 255, 0.25)',
};

/**
 * Returns a function that finds the topmost feature in `layer` under the cursor, or
 * `null` when the layer is hidden / nothing is hit. Used to find which annotation the
 * pointer is over.
 */
const createTopFeatureFinder =
	(layer, { hitTolerance = 2 } = {}) =>
	(map, e) => {
		if (!layer?.getVisible()) return null;
		let hovered = null;
		map.forEachFeatureAtPixel(
			e.pixel,
			feature => {
				hovered = feature;
				return true; // stop at the first (topmost) hit
			},
			{ layerFilter: l => l === layer, hitTolerance }
		);
		return hovered;
	};

/**
 * Builds a `tile_name -> OpenLayers polygon Feature` lookup for the grid cells that cover
 * annotation data, tagging each with its annotation `count`. The OL features are created
 * once here and reused, so hovering in and out never re-creates geometry.
 */
const buildGridFeatureLookup = (gridFeatures = [], countByTile = new Map()) => {
	const lookup = new Map();
	for (const { geometry, properties } of gridFeatures) {
		const tileName = properties?.tile_name;
		const count = tileName ? countByTile.get(tileName) : undefined;
		if (count === undefined) continue;
		const feature = createFeature(geometry, { ...properties, count });
		if (feature) lookup.set(tileName, feature);
	}
	return lookup;
};

/** Top-centre coordinate of a polygon feature's extent (where the count overlay anchors). */
const topCenterOf = polygonFeature => {
	const geometry = polygonFeature?.getGeometry?.();
	if (!geometry) return null;
	const [minX, , maxX, maxY] = geometry.getExtent();
	return [(minX + maxX) / 2, maxY];
};

/**
 * Self-contained grid overlay for the training-data layer. Owns its own toggle button
 * (rendered into the LayerSettings controls row), the grid WebGL layer, hover tracking
 * and rendering. Pulls map/project data from context and fetches its own grid + annotation
 * data via React Query (cache is shared with the parent by query key).
 *
 * Rendering:
 *  - a WebGL layer draws the polygon geometry (fills/strokes),
 *  - an HTML overlay (like the detection tooltip) shows the hovered tile's annotation
 *    count, anchored above the tile so it stays a fixed size and tracks zoom.
 *
 *  - grid off                  -> hidden
 *  - hovering a cell            -> only that cell + its count overlay
 *  - grid on, nothing hovered   -> every cell that covers annotation data (no counts)
 *
 * @param {Object} props
 * @param {import('ol/layer/Layer').default|null} props.annotationLayer - training-data layer
 *        (used to hit-test hovers and to hide the grid when the layer is switched off)
 * @param {string} props.layerId - base id; the grid layer registers as `${layerId}-grid`
 * @param {number} props.zIndex - stacking order for the grid polygon layer
 */
const TrainingDataGridLayer = ({ annotationLayer, layerId, zIndex }) => {
	const { mapObject, project, pickedTask, toolBarVisible } = useProject();
	const { uuid: projectUuid } = project ?? {};
	const { model_uuid: modelUuid } = pickedTask ?? {};

	// WebGL layer for the polygons (created/torn down by the shared hook).
	const gridLayer = useWebGLVectorLayer(mapObject, `${layerId}-grid`, {
		name: 'Grid',
		zIndex,
		visible: false,
		style: GRID_STYLE,
	});

	const [gridVisible, setGridVisible] = useState(false);
	// tile_name of the annotation currently under the cursor (drives the single-cell view)
	const [hoveredTileName, setHoveredTileName] = useState(null);

	// HTML overlay element + instance for the count "tooltip".
	const countTooltipRef = useRef(null);
	const countTooltipOverlayRef = useRef(null);

	// Annotation data drives which tiles to highlight and their counts. Same query key as
	// the parent, so React Query serves it from cache rather than refetching.
	const { data: annotationData } = useQuery({
		queryKey: ['fetchTrainingData', projectUuid, modelUuid],
		queryFn: () => getMasterFeatures(projectUuid, modelUuid, 'annotation_polygon'),
		enabled: !!projectUuid && !!modelUuid,
	});
	const hasAnnotationData = !!annotationData?.features?.length;

	// tile_name -> number of annotations in that tile
	const annotationCountByTile = useMemo(() => {
		const counts = new Map();
		(annotationData?.features ?? []).forEach(({ properties }) => {
			const tileName = properties?.tile_name;
			if (!tileName) return;
			counts.set(tileName, (counts.get(tileName) ?? 0) + 1);
		});
		return counts;
	}, [annotationData]);

	const { data: gridData } = useQuery({
		queryKey: ['fetchGrid', projectUuid, modelUuid],
		queryFn: () => getGrid({ project_uuid: projectUuid, model_uuid: modelUuid }),
		enabled: !!projectUuid && !!modelUuid && hasAnnotationData,
	});

	// tile_name -> polygon OL Feature (with count), built once per data change.
	const gridFeatureByTileName = useMemo(() => {
		const gridFeatures = gridData?.grid_geojson?.features ?? [];
		return buildGridFeatureLookup(gridFeatures, annotationCountByTile);
	}, [gridData, annotationCountByTile]);

	// Count shown in the overlay for the currently hovered tile (null = hidden).
	const hoveredCount =
		gridVisible && hoveredTileName ? annotationCountByTile.get(hoveredTileName) ?? 0 : null;

	// Create the HTML count overlay once (mirrors the detection tooltip), tear down on unmount.
	//hoveredTileName changes constantly as the mouse moves over tiles, so this runs very often.
	//It only repositions the existing overlay. No creating, no tearing down.
	useEffect(() => {
		if (!mapObject || !countTooltipRef.current) return;
		const overlay = createTooltip({
			mapRef: mapObject,
			tooltipRef: countTooltipRef.current,
			offset: [0, -12],
			positioning: 'bottom-center', // box sits above the anchor point
			id: `${layerId}-grid-count`,
		});
		countTooltipOverlayRef.current = overlay;
		return () => {
			mapObject.removeOverlay(overlay);
			countTooltipOverlayRef.current = null;
		};
	}, [mapObject, layerId]);

	// Anchor the count overlay to the top-centre of the hovered tile (or hide it).
	useEffect(() => {
		const overlay = countTooltipOverlayRef.current;
		if (!overlay) return;
		const showOverlay = gridVisible && hoveredTileName;
		const feature = showOverlay ? gridFeatureByTileName.get(hoveredTileName) : null;
		overlay.setPosition(feature ? topCenterOf(feature) : undefined);
	}, [gridVisible, hoveredTileName, gridFeatureByTileName]);

	// Clear the grid when an AI-accuracy (training) or detection flow starts. Both flows
	// hide the toolbar (`toolBarVisible: false`) while active, so we treat that as the
	// signal to turn the overlay off; the render effect then clears the grid.
	useEffect(() => {
		if (!toolBarVisible) setGridVisible(false);
	}, [toolBarVisible]);

	// Hide the grid when the training-data layer itself is switched off.
	useEffect(() => {
		if (!annotationLayer) return;
		const key = annotationLayer.on(
			'change:visible',
			() => annotationLayer.getVisible() || setGridVisible(false)
		);
		return () => unByKey(key);
	}, [annotationLayer]);

	// Track the annotation under the cursor while the grid is on, so the overlay can
	// collapse from "all cells" to "just the hovered cell". Inactive (and cleared) otherwise.
	useEffect(() => {
		if (!mapObject || !annotationLayer || !gridVisible) {
			setHoveredTileName(null);
			return;
		}

		const findTopFeature = createTopFeatureFinder(annotationLayer);
		const moveKey = mapObject.on('pointermove', e => {
			if (e.dragging) return;
			const feature = findTopFeature(mapObject, e);
			setHoveredTileName(feature?.get('tile_name') ?? null);
		});

		return () => unByKey(moveKey);
	}, [mapObject, annotationLayer, gridVisible]);

	// Render the polygon layer: all covering cells, the hovered one only, or nothing.
	// The source is mutated (clear/add) rather than replaced so WebGL re-renders reliably.
	useEffect(() => {
		if (!gridLayer) return;

		const source = gridLayer.getSource();
		source.clear();

		const features = !gridVisible
			? []
			: hoveredTileName
				? [gridFeatureByTileName.get(hoveredTileName)].filter(Boolean)
				: [...gridFeatureByTileName.values()];

		if (features.length) source.addFeatures(features);
		gridLayer.setVisible(features.length > 0);
	}, [gridLayer, gridVisible, hoveredTileName, gridFeatureByTileName]);

	return (
		<>
			<LayerFunction
				title={gridVisible ? 'Hide grid' : 'Show grid'}
				icon={<LuColumns3 />}
				onClick={() => setGridVisible(prev => !prev)}
			/>
			<div ref={countTooltipRef} className="ol-tooltip">
				{hoveredCount != null && <span>Annotation count: {hoveredCount}</span>}
			</div>
		</>
	);
};

export default TrainingDataGridLayer;
